import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { automationTestIds } from "../../testing/automationTestIds";

const DROPDOWN_PANEL_WIDTH_PX = 368;
const VIEWPORT_EDGE_PADDING_PX = 16;

const DateFilterDropdown = ({
  dateRange,
  setDateRange,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  testId,
}) => {
  const baseTestId = testId ?? automationTestIds.dateFilterDropdownDefault;
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const presetRefs = useRef([]);
  const { t } = useTranslation();

  const [tempStartDate, setTempStartDate] = useState(customStartDate);
  const [tempEndDate, setTempEndDate] = useState(customEndDate);
  const [isOpen, setIsOpen] = useState(false);
  const [alignEnd, setAlignEnd] = useState(false);

  const today = useMemo(() => new Date(), []);
  const minStartDate = useMemo(() => {
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() - 180);
    return minDate;
  }, [today]);
  const maxEndDate = useMemo(() => {
    if (!tempStartDate) return today;
    const maxFromStartDate = new Date(tempStartDate);
    maxFromStartDate.setDate(maxFromStartDate.getDate() + 180);
    return maxFromStartDate < today ? maxFromStartDate : today;
  }, [tempStartDate, today]);
  const minEndDate = useMemo(() => {
    return tempStartDate || minStartDate;
  }, [tempStartDate, minStartDate]);

  const updateMenuAlignment = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const availableWidth = Math.min(
      DROPDOWN_PANEL_WIDTH_PX,
      window.innerWidth - VIEWPORT_EDGE_PADDING_PX * 2,
    );
    const wouldOverflowRight =
      rect.left + availableWidth > window.innerWidth - VIEWPORT_EDGE_PADDING_PX;

    setAlignEnd(wouldOverflowRight);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuAlignment();
    window.addEventListener("resize", updateMenuAlignment);
    return () => window.removeEventListener("resize", updateMenuAlignment);
  }, [isOpen, updateMenuAlignment]);

  const presetOptions = [
    { label: t("verificationApprovals:last_7_days"), value: "recent" },
    { label: t("verificationApprovals:last_30_days"), value: "last30days" },
    { label: t("verificationApprovals:last_3_months"), value: "last3months" },
    { label: t("verificationApprovals:last_6_months"), value: "last6months" },
    { label: t("verificationApprovals:all"), value: "all" },
  ];

  const handlePresetSelect = (preset) => {
    setDateRange(preset);
    setCustomStartDate(null);
    setCustomEndDate(null);
    closeDropdown({ returnFocus: true });
  };

  const handleApplyCustomDate = () => {
    if (tempStartDate && tempEndDate && tempEndDate >= tempStartDate) {
      setCustomStartDate(tempStartDate);
      setCustomEndDate(tempEndDate);
      setDateRange("custom");
      closeDropdown({ returnFocus: true });
    }
  };

  const closeDropdown = ({ returnFocus = false } = {}) => {
    setIsOpen(false);
    if (returnFocus) {
      setTimeout(() => triggerRef.current?.focus(), 0);
    }
  };

  const openDropdown = ({ focusFirstPreset = false } = {}) => {
    updateMenuAlignment();
    setIsOpen(true);
    if (focusFirstPreset) {
      setTimeout(() => presetRefs.current[0]?.focus(), 0);
    }
  };

  const handleTriggerKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      openDropdown({ focusFirstPreset: true });
    }
  };

  const handleDropdownKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeDropdown({ returnFocus: true });
    }
  };

  const focusPresetAt = (index) => {
    const presetCount = presetOptions.length;
    const nextIndex = (index + presetCount) % presetCount;
    presetRefs.current[nextIndex]?.focus();
  };

  const handlePresetKeyDown = (e, index) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusPresetAt(index + 1);
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      focusPresetAt(index - 1);
      return;
    }

    handleDropdownKeyDown(e);
  };

  const getDisplayText = () => {
    if (dateRange === "custom" && customStartDate && customEndDate) {
      return `${format(customStartDate, "MMM d, yyyy")} - ${format(
        customEndDate,
        "MMM d, yyyy",
      )}`;
    }
    return (
      presetOptions.find((opt) => opt.value === dateRange)?.label ||
      t("verificationApprovals:last_7_days")
    );
  };

  return (
    <div
      className="relative inline-block text-left w-full"
      ref={dropdownRef}
      id={baseTestId}
    >
      <button
        ref={triggerRef}
        type="button"
        id={`${baseTestId}-trigger`}
        className="flex justify-between items-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2"
        onClick={() => (isOpen ? closeDropdown() : openDropdown())}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={`${baseTestId}-menu`}
      >
        <span className="min-w-0 truncate">{getDisplayText()}</span>
        <svg
          className="-mr-1 ml-2 h-5 w-5 shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className={`absolute mt-3 w-[23rem] max-w-[calc(100vw-2rem)] rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50 ${
            alignEnd
              ? "right-0 left-auto origin-top-right"
              : "left-0 origin-top-left"
          }`}
          id={`${baseTestId}-menu`}
          role="dialog"
          tabIndex={-1}
          aria-labelledby={`${baseTestId}-trigger`}
        >
          <div
            role="menu"
            aria-orientation="vertical"
            aria-labelledby={`${baseTestId}-trigger`}
          >
            {presetOptions.map((option, index) => (
              <button
                ref={(element) => {
                  presetRefs.current[index] = element;
                }}
                key={option.value}
                type="button"
                id={`${baseTestId}-preset-${option.value}`}
                role="menuitemradio"
                aria-checked={dateRange === option.value}
                className={`${
                  dateRange === option.value
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-700"
                } block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-inset`}
                onClick={() => handlePresetSelect(option.value)}
                onKeyDown={(e) => handlePresetKeyDown(e, index)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <hr className=" border-gray-200" />

          <div className="px-4 py-2 text-sm text-gray-500 font-medium">
            {t("verificationApprovals:custom_period")}:
          </div>

          <div className="px-4 py-2 space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div
                className="relative w-full sm:w-[10rem]"
                id={`${baseTestId}-datepicker-start`}
              >
                <span id={`${baseTestId}-start-label`} className="sr-only">
                  {t("verificationApprovals:start_date")}
                </span>
                <DatePicker
                  selected={tempStartDate}
                  onChange={(date) => {
                    setTempStartDate(date);

                    if (!date) {
                      setTempEndDate(null);
                      return;
                    }

                    if (tempEndDate && tempEndDate < date) {
                      setTempEndDate(null);
                      return;
                    }

                    const maxFromStart = new Date(date);
                    maxFromStart.setDate(maxFromStart.getDate() + 180);
                    const upperBound =
                      maxFromStart < today ? maxFromStart : today;

                    if (tempEndDate && tempEndDate > upperBound) {
                      setTempEndDate(null);
                    }
                  }}
                  onKeyDown={handleDropdownKeyDown}
                  selectsStart
                  startDate={tempStartDate}
                  endDate={tempEndDate}
                  dateFormat={"dd/MM/yyyy"}
                  className="w-full border border-gray-300 rounded pl-3 pr-12 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholderText={t("verificationApprovals:start_date")}
                  ariaLabelledBy={`${baseTestId}-start-label`}
                  minDate={minStartDate}
                  maxDate={today}
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  yearDropdownItemNumber={15}
                  scrollableYearDropdown
                />
                <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
              </div>

              <div
                className="relative w-full sm:w-[10rem]"
                id={`${baseTestId}-datepicker-end`}
              >
                <span id={`${baseTestId}-end-label`} className="sr-only">
                  {t("verificationApprovals:end_date")}
                </span>
                <DatePicker
                  selected={tempEndDate}
                  onChange={(date) => setTempEndDate(date)}
                  onKeyDown={handleDropdownKeyDown}
                  selectsEnd
                  startDate={tempStartDate}
                  endDate={tempEndDate}
                  dateFormat={"dd/MM/yyyy"}
                  minDate={minEndDate}
                  maxDate={maxEndDate}
                  className="w-full border border-gray-300 rounded pl-12 pr-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholderText={t("verificationApprovals:end_date")}
                  ariaLabelledBy={`${baseTestId}-end-label`}
                  disabled={!tempStartDate}
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  yearDropdownItemNumber={15}
                  scrollableYearDropdown
                />
                <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
              </div>
            </div>
            {!tempStartDate ? (
              <div className="text-xs text-gray-500">
                {t("verificationApprovals:select_start_date_between")} must be
                between {format(minStartDate, "MMM d, yyyy")}{" "}
                {t("verificationApprovals:and")} {format(today, "MMM d, yyyy")}
              </div>
            ) : (
              <div className="text-xs text-gray-500">
                {t("verificationApprovals:end_date_must_be_between")}{" "}
                {format(tempStartDate, "MMM d, yyyy")}{" "}
                {t("verificationApprovals:and")} {format(today, "MMM d, yyyy")}
              </div>
            )}

            <button
              type="button"
              id={`${baseTestId}-apply`}
              onClick={handleApplyCustomDate}
              onKeyDown={handleDropdownKeyDown}
              disabled={
                !tempStartDate || !tempEndDate || tempEndDate < tempStartDate
              }
              className="w-full bg-cfPrimaryButton text-white font-medium py-2 px-4 rounded-md text-sm hover:bg-cfPrimaryDark disabled:opacity-50 disabled:cursor-not-allowed mt-3"
            >
              {t("verificationApprovals:apply")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilterDropdown;
